import React, { useCallback, useContext, useEffect, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CanvasConfigProps, CanvasConfigValue, CanvasGridCols } from './types';
import LibraryTitleInput from '../library-title-input';
import CustomTooltip from '../../Tooltip/CustomTooltip';
import { SingleCanvasIcon } from '../../../bombiix-icon';
import { NewCheckbox } from '../../PIM/common/NewCheckbox';
import { NewInput } from '../../PIM/common/NewInput';
import FieldSetupPanelContext from './field-setup-panel.context';

const CANVAS_GRID_OPTIONS: { count: CanvasGridCols; iconClass: string }[] = [
  { count: 1, iconClass: 'icon-Grid' },
  { count: 2, iconClass: 'icon-Grid2' },
  { count: 3, iconClass: 'icon-Grid3' },
  { count: 4, iconClass: 'icon-Grid4' },
  { count: 6, iconClass: 'icon-Grid6' },
];

const CanvasConfig: React.FC<CanvasConfigProps> = ({ value, onChange, className = '', disabled = false, showLibraryTitle = true, hideTitle = false }) => {
  const { t } = useTranslation('custom-module-system-canvas');
  const setupPanel = useContext(FieldSetupPanelContext);
  const isInSetupCard = setupPanel !== null;
  const showOptionsPart = setupPanel !== 'setup';
  const normalizedValue: CanvasConfigValue = React.useMemo(
    () => ({ title: value?.title ?? '', required: value?.required ?? false, defaultGridCols: value?.defaultGridCols ?? 1 }),
    [value],
  );

  const [localValue, setLocalValue] = useState<CanvasConfigValue>(normalizedValue);
  const [libraryTitle, setLibraryTitle] = useState<string>('');

  useEffect(() => {
    setLocalValue(normalizedValue);
  }, [normalizedValue]);

  const updateTitle = useCallback(
    (next: string) => {
      const nextVal = { ...localValue, title: next };
      setLocalValue(nextVal);
      onChange(nextVal);
    },
    [localValue, onChange],
  );

  const toggleRequired = useCallback(() => {
    const nextVal = { ...localValue, required: !localValue.required };
    setLocalValue(nextVal);
    onChange(nextVal);
  }, [localValue, onChange]);

  const handleLibrarySave = useCallback(() => {
    setLibraryTitle('');
  }, []);

  const handleLibrarySelect = useCallback(
    (controlTypeData: unknown) => {
      const fetched = (controlTypeData as { content?: { content?: CanvasConfigValue } })?.content?.content;
      if (fetched) {
        setLocalValue(fetched);
        onChange(fetched);
      }
    },
    [onChange],
  );

  const handleGridColChange = useCallback(
    (cols: CanvasGridCols) => {
      const nextVal = { ...localValue, defaultGridCols: cols };
      setLocalValue(nextVal);
      onChange(nextVal);
    },
    [localValue, onChange],
  );

  return (
    <div className={`cm-maxw-520 ${className}`}>
      {showLibraryTitle && (
        <LibraryTitleInput
          value={libraryTitle}
          onChange={setLibraryTitle}
          onSave={handleLibrarySave}
          onSelect={handleLibrarySelect}
          controlType="canvas"
          contentData={localValue}
          placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER_LIBRARY_TITLE')}
        />
      )}
      {!hideTitle && (
      <div className="mb-2">
        <p className="m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CANVAS.TITLE')}</p>
        <NewInput
          type="text"
          placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER')}
          value={localValue.title}
          onChange={(e) => updateTitle(e.target.value)}
          disabled={disabled}
        />
      </div>
      )}
      {!isInSetupCard && (
      <div className="mb-1 mt-2 d-flex align-items-center cm-checkbox">
        <label className="m-0 mr-1">{t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')}</label>
        {!localValue.title?.trim() ? (
          <CustomTooltip tooltipText={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_CHECKBOX_TOOLTIP')}>
            <NewCheckbox className="mb-1 mr-0" checked={!!localValue.required} onChange={toggleRequired} disabled={disabled || !localValue.title?.trim()} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
          </CustomTooltip>
        ) : (
          <NewCheckbox className="mb-1 mr-0" checked={!!localValue.required} onChange={toggleRequired} disabled={disabled} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
        )}
      </div>
      )}
      {showOptionsPart && (
      <div className={isInSetupCard ? 'mb-2' : 'mb-2 mt-3'}>
        <p className="m-0 mb-1">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CANVAS.DEFAULT_GRID_COLS')}</p>
        <div className="d-flex align-items-center">
          {CANVAS_GRID_OPTIONS.map(({ count, iconClass }) => (
            <button
              key={count}
              type="button"
              aria-label="grid"
              className={`${localValue.defaultGridCols === count ? 'active' : ''} btn btn-outline md`}
              onClick={() => handleGridColChange(count)}
              disabled={disabled}
              title={`${count} ${t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CANVAS.COLUMNS')}`}
            >
              <i className={`${iconClass} f-28`} />
            </button>
          ))}
        </div>
      </div>
      )}
      {!isInSetupCard && (
      <div className="mt-3 d-flex align-items-center gap-2 p-2">
        <SingleCanvasIcon size={24} />
        <p className="m-0 f-12 text-muted">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.CANVAS.PREVIEW_HINT')}</p>
      </div>
      )}
    </div>
  );
};

export default memo(CanvasConfig);
